import React, { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import { lazyWithRetry } from "../core/lazy_with_retry";
import { Location } from "../data";
import { Icon } from "./icon";
import { WebMercatorViewport } from "react-map-gl";

const ReactMapGL = lazyWithRetry(() => import("react-map-gl"));
const Marker = lazyWithRetry(() =>
  import("react-map-gl").then((module) => {
    return { default: module.Marker };
  })
);

interface MapboxProps {
  latitude: number | null;
  longitude: number | null;
}

const DEFAULT_VIEWPORT_ZOOM = 15.0;

export function Mapbox(props: MapboxProps) {
  const { latitude, longitude } = props;
  const [viewport, setViewport] = useState({
    width: "100%",
    height: 364,
    latitude: latitude || 37.7915,
    longitude: longitude || -122.4121,
    zoom: DEFAULT_VIEWPORT_ZOOM,
  });

  const markerCoordinates = useMemo(() => {
    return {
      latitude: latitude || 37.7915,
      longitude: longitude || -122.4121,
    };
  }, [latitude, longitude]);

  useEffect(() => {
    setViewport((prevViewport) => ({
      ...prevViewport,
      latitude: latitude || 37.7915,
      longitude: longitude || -122.4121,
    }));
  }, [latitude, longitude]);

  return (
    <Fragment>
      <Suspense fallback={<div>Loading map...</div>}>
        {/*
        // @ts-ignore */}
        <ReactMapGL
          {...viewport}
          mapboxApiAccessToken="pk.eyJ1Ijoia2hhbmhkYW8iLCJhIjoiY2tiOTh1bzhqMGJycTMybWU0eXJ1eGd5OSJ9.FCGGc10CpwBElRTwWK77ew"
          onViewportChange={(viewport: any) => {
            if (viewport.width === 0 || viewport.height === 0) {
              return;
            }

            setViewport({
              width: String(viewport.width),
              height: 364,
              latitude: viewport.latitude,
              longitude: viewport.longitude,
              zoom: viewport.zoom,
            });
          }}
        >
          {/*
          // @ts-ignore */}
          <Marker
            latitude={markerCoordinates.latitude}
            longitude={markerCoordinates.longitude}
            // @ts-ignore: https://github.com/visgl/react-map-gl/issues/1052
            offsetLeft={-32}
            offsetTop={-64}
          >
            <Icon
              className="mapbox-location-pin"
              name="location_pin"
              size={64}
            />
          </Marker>
        </ReactMapGL>
      </Suspense>
    </Fragment>
  );
}

interface MembershipMapboxProps {
  locationList: Location[];
}

const applyToArray = (func: Math["min"] | Math["max"], array: number[]) =>
  func.apply(Math, array);

// If there are multiple locations, ensure that the map is centered and zoomed to fit all locations
const getBoundsForPoints = (points: Location[]) => {
  // Calculate corner values of bounds
  const pointsLong = points.map((point) => Number(point.longitude));
  const pointsLat = points.map((point) => Number(point.latitude));
  const cornersLongLat = [
    [applyToArray(Math.min, pointsLong), applyToArray(Math.min, pointsLat)],
    [applyToArray(Math.max, pointsLong), applyToArray(Math.max, pointsLat)],
  ];

  // Use WebMercatorViewport to get center longitude/latitude and zoom
  const viewport = new WebMercatorViewport({ width: 800, height: 600 })
    // @ts-ignore
    .fitBounds(cornersLongLat, { padding: 200 });
  const { longitude, latitude, zoom } = viewport;

  return {
    longitude,
    latitude,
    zoom: zoom > DEFAULT_VIEWPORT_ZOOM ? DEFAULT_VIEWPORT_ZOOM : zoom,
  };
};

export function MembershipMapbox(props: MembershipMapboxProps) {
  const { locationList } = props;

  const [viewport, setViewport] = useState({
    width: "100%",
    height: 364,
    ...getBoundsForPoints(locationList),
  });

  return (
    <Fragment>
      <Suspense fallback={<div>Loading map...</div>}>
        <ReactMapGL
          {...viewport}
          mapboxApiAccessToken="pk.eyJ1Ijoia2hhbmhkYW8iLCJhIjoiY2tiOTh1bzhqMGJycTMybWU0eXJ1eGd5OSJ9.FCGGc10CpwBElRTwWK77ew"
          onViewportChange={(viewport: any) => {
            if (viewport.width === 0 || viewport.height === 0) {
              return;
            }

            setViewport({
              width: String(viewport.width),
              height: 364,
              latitude: viewport.latitude,
              longitude: viewport.longitude,
              zoom: viewport.zoom,
            });
          }}
        >
          {locationList?.map((location) => {
            if (!location.latitude || !location.longitude) {
              return null;
            }

            return (
              <Marker
                anchor="bottom"
                key={location.id}
                latitude={location.latitude}
                longitude={location.longitude}
                // @ts-ignore: https://github.com/visgl/react-map-gl/issues/1052
                offsetLeft={-20}
                offsetTop={-40}
              >
                <Icon
                  className="mapbox-location-pin"
                  size={40}
                  name="location_pin"
                />
              </Marker>
            );
          })}
        </ReactMapGL>
      </Suspense>
    </Fragment>
  );
}
